import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import Combobox from '../components/Combobox';
import DataTable from '../components/DataTable';
import MonthSelect from '../components/MonthSelect';

const money = (n) => `${Math.round(Number(n || 0)).toLocaleString('ru')} ₽`;
const monthLabel = (m) => m ? new Date(m).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }) : '—';

// Короткая подпись тарифа для выпадающего списка — полное описание сложности длинное
// (многострочное), в кнопке/списке достаточно типа, первой части описания и цены.
const rateLabel = (r) => `${r.sketch_type} — ${r.complexity_label.split(/[:.]/)[0].trim()} (${money(r.base_cost)})`;

function AssignModal({ projects, designers, onClose, onSaved }) {
  const [projectId, setProjectId] = useState('');
  const [designerIds, setDesignerIds] = useState([]);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleDesigner = (id) => {
    setDesignerIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };

  // У одного проекта может быть сразу несколько дизайнеров (основной эскиз + доработки и т.д.) —
  // заводим отдельную строку-назначение на каждого выбранного, у каждого своя оплата и статус.
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!projectId || designerIds.length === 0) { setError('Выберите проект и хотя бы одного дизайнера'); return; }
    setSaving(true);
    try {
      for (const designerId of designerIds) {
        await api.post('/design-tasks', { project_id: projectId, designer_id: designerId, comment: comment || null });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h2>Назначить проект дизайнеру</h2><button className="modal-close" onClick={onClose}>×</button></div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Проект *</label>
            <Combobox value={projectId} onChange={setProjectId} options={projects.map(p => ({ value: p.id, label: p.name }))} placeholder="Начните вводить название проекта" />
          </div>
          <div className="form-group">
            <label>Дизайнер (можно выбрать несколько) *</label>
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 8, maxHeight: 180, overflowY: 'auto' }}>
              {designers.length === 0 && <div className="text-muted" style={{ fontSize: 13 }}>Нет доступных дизайнеров</div>}
              {designers.map(d => (
                <label key={d.id} className="col-settings-item">
                  <input type="checkbox" checked={designerIds.includes(d.id)} onChange={() => toggleDesigner(d.id)} />
                  {d.name}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Комментарий</label>
            <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder="Например: нужен эскиз для КП" />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Сохранение...' : `Назначить${designerIds.length > 1 ? ` (${designerIds.length})` : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Помесячная зарплата одного дизайнера — по мотивам таблички в Google-таблице заказчика:
// выбрал дизайнера, увидел разбивку заработанного по месяцам. Считаем по тем же данным,
// что уже загружены в основную таблицу (design-tasks), без отдельного запроса к серверу —
// сумма берётся только с готовых задач (is_done), сгруппированных по completion_month.
function SalaryByMonth({ tasks }) {
  const designerOptions = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      if (!map.has(t.designer_id)) map.set(t.designer_id, t.designer_name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [tasks]);

  const [designerId, setDesignerId] = useState('');

  useEffect(() => {
    if (!designerId && designerOptions.length > 0) setDesignerId(designerOptions[0].value);
  }, [designerOptions, designerId]);

  const byMonth = useMemo(() => {
    if (!designerId) return [];
    const map = {};
    for (const t of tasks) {
      if (t.designer_id !== designerId || !t.is_done) continue;
      const key = t.completion_month ? t.completion_month.slice(0, 7) : 'Без даты';
      map[key] = (map[key] || 0) + Number(t.total_amount || 0);
    }
    return Object.entries(map)
      .map(([month, sum]) => ({ month, sum }))
      .sort((a, b) => (a.month === 'Без даты' ? 1 : b.month === 'Без даты' ? -1 : a.month.localeCompare(b.month)));
  }, [tasks, designerId]);

  const total = byMonth.reduce((s, m) => s + m.sum, 0);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, maxWidth: 320, marginBottom: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Зарплата дизайнера по месяцам</div>
      <Combobox value={designerId} onChange={setDesignerId} options={designerOptions} placeholder="Выберите дизайнера" />
      {designerId && (
        <table style={{ marginTop: 10, width: '100%' }}>
          <tbody>
            {byMonth.length === 0 ? (
              <tr><td className="text-muted" style={{ padding: '4px 0' }}>Готовых задач нет</td></tr>
            ) : byMonth.map(m => (
              <tr key={m.month}>
                <td style={{ padding: '2px 0' }}>{m.month === 'Без даты' ? m.month : monthLabel(`${m.month}-01`)}</td>
                <td style={{ padding: '2px 0', textAlign: 'right' }}>{money(m.sum)}</td>
              </tr>
            ))}
            {byMonth.length > 0 && (
              <tr style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '4px 0', fontWeight: 600 }}>Итого</td>
                <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 600 }}>{money(total)}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Designers() {
  const { user } = useAuth();
  const canManage = ['admin', 'manager', 'rop'].includes(user.role);
  const canViewSalaryWidget = user.role === 'admin' || user.role === 'rop';
  const [tasks, setTasks] = useState([]);
  const [rates, setRates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [savingIds, setSavingIds] = useState(() => new Set());

  const load = () => {
    setLoading(true);
    api.get('/design-tasks').then(r => setTasks(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/design-tasks/rates').then(r => setRates(r.data));
    if (canManage) {
      // Список проектов с id нужен для назначения — берём из /finance/deals (там же, где
      // сделки), а не из /payments/meta, который отдаёт проекты только именами без id.
      api.get('/finance/deals').then(r => setProjects(r.data.map(p => ({ id: p.id, name: p.name })))).catch(() => {});
      api.get('/design-tasks/designers').then(r => setDesigners(r.data));
    }
  }, []);

  const totalPending = useMemo(() => tasks.filter(t => !t.is_done).length, [tasks]);

  // Строки таблицы не перезагружаются целиком на каждый чих — сохранение конкретной задачи
  // просто рефетчит список (как и раньше через onChanged), а savingIds лишь блокирует поля
  // именно той строки, которая сейчас сохраняется, остальные остаются кликабельными.
  const save = async (taskId, fields) => {
    setSavingIds(s => new Set(s).add(taskId));
    try {
      await api.patch(`/design-tasks/${taskId}`, fields);
      const r = await api.get('/design-tasks');
      setTasks(r.data);
    } catch (err) {
      window.alert(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSavingIds(s => { const n = new Set(s); n.delete(taskId); return n; });
    }
  };

  const columns = useMemo(() => [
    { key: 'project_name', label: 'Проект' },
    {
      key: 'designer_name', label: 'Дизайнер', sortable: false,
      render: t => {
        if (!canManage) return t.designer_name;
        // Список от /design-tasks/designers отдаёт только активных — если строка сейчас
        // назначена на уже деактивированного дизайнера (например, служебный «Отдел дизайна»),
        // его самого нужно всё равно показать в списке, иначе Combobox не найдёт подпись
        // для текущего value и покажет пустое поле вместо имени.
        const options = designers.some(d => d.id === t.designer_id)
          ? designers
          : [...designers, { id: t.designer_id, name: t.designer_name }];
        return (
          <Combobox
            value={t.designer_id}
            onChange={(v) => save(t.id, { designer_id: v })}
            options={options.map(d => ({ value: d.id, label: d.name }))}
            disabled={savingIds.has(t.id)}
          />
        );
      }
    },
    {
      key: 'sketch', label: 'Тип / сложность эскиза', sortable: false,
      filterValue: t => t.sketch_type ? `${t.sketch_type} — ${t.complexity_label}` : '—',
      render: t => {
        const canEditContent = t.designer_id === user.id || canManage;
        return canEditContent ? (
          <Combobox
            value={t.rate_id || ''}
            onChange={(v) => save(t.id, { rate_id: v })}
            options={rates.map(r => ({ value: r.id, label: rateLabel(r) }))}
            placeholder="Выберите тип и сложность эскиза"
            disabled={savingIds.has(t.id)}
          />
        ) : (t.sketch_type ? `${t.sketch_type} — ${t.complexity_label}` : '—');
      }
    },
    {
      key: 'is_urgent', label: 'Срочность', sortable: false,
      filterValue: t => t.is_urgent ? 'Да' : 'Нет',
      render: t => {
        const canEditContent = t.designer_id === user.id || canManage;
        return (
          <label>
            <input type="checkbox" checked={t.is_urgent} disabled={!canEditContent || savingIds.has(t.id)} onChange={e => save(t.id, { is_urgent: e.target.checked })} />
          </label>
        );
      }
    },
    {
      key: 'is_fast', label: 'Скорость', sortable: false,
      filterValue: t => t.is_fast ? 'Да' : 'Нет',
      render: t => {
        const canEditContent = t.designer_id === user.id || canManage;
        return (
          <label>
            <input type="checkbox" checked={t.is_fast} disabled={!canEditContent || savingIds.has(t.id)} onChange={e => save(t.id, { is_fast: e.target.checked })} />
          </label>
        );
      }
    },
    {
      key: 'extra_bonus', label: 'Доп. бонус', filterable: false, sortValue: t => Number(t.extra_bonus),
      render: t => canManage ? (
        <input type="number" step="0.01" style={{ width: 90 }} value={t.extra_bonus} disabled={savingIds.has(t.id)}
          onChange={e => save(t.id, { extra_bonus: parseFloat(e.target.value) || 0 })} />
      ) : money(t.extra_bonus)
    },
    { key: 'total_amount', label: 'Сумма', filterable: false, sortValue: t => Number(t.total_amount), render: t => <b>{money(t.total_amount)}</b> },
    {
      key: 'is_done', label: 'Готово', sortable: false,
      filterValue: t => t.is_done ? 'Да' : 'Нет',
      render: t => {
        const canEditContent = t.designer_id === user.id || canManage;
        return (
          <label>
            <input type="checkbox" checked={t.is_done} disabled={!canEditContent || savingIds.has(t.id)} onChange={e => save(t.id, { is_done: e.target.checked })} />
          </label>
        );
      }
    },
    {
      key: 'completion_month', label: 'Месяц выполнения',
      filterValue: t => monthLabel(t.completion_month),
      sortValue: t => t.completion_month || '',
      render: t => canManage ? (
        <MonthSelect value={t.completion_month ? t.completion_month.slice(0, 7) : ''} disabled={savingIds.has(t.id)}
          onChange={v => save(t.id, { completion_month: v ? `${v}-01` : null })} />
      ) : monthLabel(t.completion_month)
    },
    {
      key: 'comment', label: 'Комментарий', filterable: false, sortable: false,
      render: t => {
        const canEditContent = t.designer_id === user.id || canManage;
        return canEditContent ? (
          <input style={{ minWidth: 160 }} value={t.comment || ''} disabled={savingIds.has(t.id)} onChange={e => save(t.id, { comment: e.target.value })} placeholder="Комментарий" />
        ) : (t.comment || '—');
      }
    }
  ], [rates, designers, savingIds, canManage, user.id]);

  return (
    <div>
      <div className="toolbar">
        <h2>Дизайнеры</h2>
        <div className="toolbar-spacer" />
        {!canManage && <span className="text-muted">В работе: {totalPending}</span>}
        {canManage && <button className="btn-primary btn-sm" onClick={() => setShowAssign(true)}>+ Назначить проект дизайнеру</button>}
      </div>

      {canViewSalaryWidget && !loading && tasks.length > 0 && <SalaryByMonth tasks={tasks} />}

      {loading ? <div className="loading-text">Загрузка...</div> : tasks.length === 0 ? (
        <div className="empty-state">{canManage ? 'Пока никому ничего не назначено' : 'У вас пока нет назначенных проектов'}</div>
      ) : (
        <DataTable
          columns={columns}
          rows={tasks}
          initialSort={{ key: 'is_done', dir: 'asc' }}
          rowClassName={t => t.is_done ? 'text-muted' : ''}
        />
      )}

      {showAssign && (
        <AssignModal
          projects={projects}
          designers={designers}
          onClose={() => setShowAssign(false)}
          onSaved={() => { setShowAssign(false); load(); }}
        />
      )}
    </div>
  );
}
